import { extractApiErrorMessage, translateOr, useAuthStore, useTranslate, employeeHomePath } from "@/shared";
import { Button, toast, TopBar } from "@/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { ConfirmSubmitDialog } from "../../components/ConfirmSubmitDialog";
import { WizardStepper } from "../../components/WizardStepper";
import { WizardSuccessCard } from "../../components/WizardSuccessCard";
import {
  isAssignmentValid,
  TechnicianAssignmentStep,
  toRows,
  toSavedAssignments,
  type AssignmentValue,
} from "../../components/steps/TechnicianAssignmentStep";
import { InstallationScopeStep, isScopeValid, type ScopeValue } from "../../components/steps/InstallationScopeStep";
import { isPlanDetailsValid, PlanDetailsStep, type PlanDetailsValue } from "../../components/steps/PlanDetailsStep";
import { isTemplateStepValid, TemplateStep, type TemplateValue } from "../../components/steps/TemplateStep";
import { WizardActionFooter } from "../../components/WizardActionFooter";
import { IngestionStatusBlocks } from "../../components/IngestionStatusBlocks";
import { pmKeys } from "../../hooks/query-keys";
import { useInstallationPlanById } from "../../hooks/use-installation-plan-by-id";
import { useInstallationPlanReviewer } from "../../hooks/use-installation-plan-reviewer";
import { useInstallationPlanScope } from "../../hooks/use-installation-plan-scope";
import { useInstallationPlanTemplates } from "../../hooks/use-installation-plan-templates";
import { useProjectById } from "../../hooks/use-project-by-id";
import { useSaveInstallationPlan } from "../../hooks/use-save-installation-plan";
import { useVendorAssignmentSearch } from "../../hooks/use-vendor-assignment-search";
import type { InstallationPlanRouteSearch } from "../../routes";
import { publishInstallationPlan, type SavedInstallationPlan } from "../../services/installation-plan";
import { checkInstallationScope } from "../../services/installation-scope";
import { validateVendorAssignment } from "../../services/vendor-assignment";
import type { InstallationPlan } from "../../types/installation-plan";
import type { Project } from "../../types/project";
import { geographyCodesKey } from "../../utils/geography";
import { pmMyProjectsPath, pmProjectDetailsPath } from "../../utils/paths";
import { tenantId } from "@/shared/config/global-config";

/** The route's search-param navigator, cast once from the untyped `useNavigate()` (see the
 *  comment at its one call site) -- named so the extracted hooks below can take it as a
 *  parameter instead of each re-declaring the same inline type. */
type PlanNavigate = (opts: {
  search: (prev: InstallationPlanRouteSearch) => InstallationPlanRouteSearch;
  replace?: boolean;
}) => Promise<void>;

export function CreateInstallationPlanPage() {
  const { t } = useTranslate();

  const STEP_DEFINITIONS = [
    { label: translateOr(t, "ES_PM_PLAN_DETAILS_STEP", "Plan Details") },
    { label: translateOr(t, "ES_PM_INSTALLATION_SCOPE_STEP", "Installation Scope") },
    { label: translateOr(t, "ES_PM_TEMPLATE_STEP", "Template") },
    { label: translateOr(t, "ES_PM_TECHNICIAN_ASSIGNMENT_STEP", "Technician Assignment") },
  ];
  // The route's path is computed at runtime via contextPath(), so there's no
  // static `Route` export to use the fully-typed search API here — read/
  // write the current route's search loosely instead (same pattern as
  // CreateProjectPage/im's InboxPage).
  const search = useSearch({ strict: false }) as InstallationPlanRouteSearch;
  const rawNavigate = useNavigate();
  const navigate = rawNavigate as PlanNavigate;

  const projectId = search.projectId;
  const planId = search.planId;
  const currentStep = search.step ?? 1;
  const { data: project } = useProjectById(projectId);
  const { data: existingPlan } = useInstallationPlanById(planId);
  const { data: assignedReviewer, isLoading: isReviewerLoading } = useInstallationPlanReviewer(planId);
  const { data: savedScope, isLoading: isScopeLoading } = useInstallationPlanScope(planId);
  const { data: savedTemplates, isLoading: isTemplatesLoading } = useInstallationPlanTemplates(planId);
  const savePlan = useSaveInstallationPlan();
  const queryClient = useQueryClient();
  const { data: vendorAssignmentSearch } = useVendorAssignmentSearch(planId, currentStep === 4);
  const assignmentRows = useMemo(() => toRows(vendorAssignmentSearch?.sites ?? []), [vendorAssignmentSearch]);
  const savedAssignments = useMemo(
    () => toSavedAssignments(vendorAssignmentSearch?.sites ?? []),
    [vendorAssignmentSearch],
  );
  const accessToken = useAuthStore((state) => state.accessToken);
  const authUser = useAuthStore((state) => state.user);

  const [planDetails, setPlanDetails] = useState<PlanDetailsValue>({
    geographyDetails: {},
    sectorCodes: [],
    reviewerCode: "",
  });
  const [scope, setScope] = useState<ScopeValue>([]);
  const [templates, setTemplates] = useState<TemplateValue>([]);
  const [assignments, setAssignments] = useState<AssignmentValue>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined);
  const [isPublishing, setIsPublishing] = useState(false);
  const [justPublished, setJustPublished] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [scopeApplied, setScopeApplied] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [isPlanHydrated, setIsPlanHydrated] = useState(!planId);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  // Step 1's "is there anything this plan could scope?" answer, when it is no. Kept apart from
  // saveError because it is guidance (amber), not a failure: the PM's selection is valid, it just
  // has nothing in it yet.
  const [scopeWarning, setScopeWarning] = useState<string | undefined>(undefined);
  const [isCheckingScope, setIsCheckingScope] = useState(false);

  usePlanDetailsHydration(planId, existingPlan, isScopeLoading, isTemplatesLoading, setPlanDetails, setIsPlanHydrated);
  useReviewerHydration(planId, assignedReviewer, setPlanDetails);
  useScopeHydration(planId, savedScope, setScope);
  useTemplatesHydration(planId, savedTemplates, setTemplates);
  useAssignmentsHydration(planId, savedAssignments, setAssignments);
  useDefaultPlanDates(planId, project, planDetails.startDate, setPlanDetails);

  const isPublished = existingPlan?.additionalDetails?.status === "PUBLISHED";
  const projectGeography = project?.additionalDetails?.geographyDetails ?? {};
  // A published plan's geography can only shrink (it takes no new sites), so its own current
  // geography is all it may pick from.
  const allowedPlanGeography = isPublished ? (existingPlan?.geographyDetails ?? {}) : projectGeography;
  const isPlanGeographyDirty =
    Boolean(existingPlan) &&
    geographyCodesKey(planDetails.geographyDetails) !== geographyCodesKey(existingPlan?.geographyDetails);
  const isPlanSectorsDirty =
    Boolean(existingPlan) &&
    sectorCodesKey(planDetails.sectorCodes) !== sectorCodesKey(existingPlan?.additionalDetails?.sectorCodes);
  const isPlanReviewerDirty = Boolean(assignedReviewer) && planDetails.reviewerCode !== assignedReviewer;
  const isPlanDetailsDirty = isPlanGeographyDirty || isPlanSectorsDirty || isPlanReviewerDirty;

  const uniqueSolutionCodes = useMemo(
    () =>
      Array.from(new Set(scope.filter((entry) => entry.included && entry.solutionCode).map((entry) => entry.solutionCode!))),
    [scope],
  );

  async function persistPlan(): Promise<SavedInstallationPlan> {
    const plan: InstallationPlan = {
      id: planId,
      tenantId: project?.tenantId ?? tenantId(),
      projectId: projectId!,
      name: existingPlan?.name,
      geographyDetails: planDetails.geographyDetails,
      startDate: planDetails.startDate,
      endDate: planDetails.endDate,
      additionalDetails: {
        ...existingPlan?.additionalDetails,
        sectorCodes: planDetails.sectorCodes,
        reviewerCode: planDetails.reviewerCode,
        scope,
        templates,
        assignments,
      },
    };
    return savePlan.mutateAsync(plan);
  }

  const isPlanDetailsComplete = isPlanDetailsValid(planDetails, project?.startDate, project?.endDate);
  const isScopeComplete = scopeApplied || isScopeValid(scope);
  const isTemplateComplete = isTemplateStepValid(templates, uniqueSolutionCodes);
  // The rail represents completed stages. A completed stage unlocks only
  // the next one, so the numbered navigation cannot skip unfinished work.
  let maxAccessibleStep: number;
  if (!planId) {
    maxAccessibleStep = 1;
  } else if (!isScopeComplete) {
    maxAccessibleStep = 2;
  } else if (!isTemplateComplete) {
    maxAccessibleStep = 3;
  } else {
    maxAccessibleStep = 4;
  }

  let canGoNext: boolean;
  if (currentStep === 1) {
    canGoNext = isPlanDetailsComplete;
  } else if (currentStep === 2) {
    canGoNext = isScopeComplete;
  } else if (currentStep === 3) {
    canGoNext = isTemplateComplete;
  } else {
    canGoNext = false;
  }

  // Scope application reports its completed entries before React has flushed
  // the child hook's final `done` state. Once those valid entries are in the
  // plan state, the footer can safely enable Next instead of waiting on that
  // stale busy flag for one more render.
  const isNavigationBusy =
    savePlan.isPending || isCheckingScope || (scopeBusy && !isScopeComplete) || templateBusy;

  // A warning about one selection says nothing about the next, so it goes as soon as the
  // geography or sectors change rather than lingering over a selection it no longer describes.
  const scopeSelectionKey = JSON.stringify([planDetails.geographyDetails, planDetails.sectorCodes]);
  useEffect(() => {
    setScopeWarning(undefined);
  }, [scopeSelectionKey]);

  /**
   * Whether a brand-new plan with this selection would have any end user site to scope. Asked
   * before creating it so a plan isn't created over a selection with no free site, which would
   * leave the PM with an empty draft to fix or abandon. Fails closed --
   * if the check itself can't run, the plan isn't created either.
   */
  async function hasAddableSites(): Promise<boolean> {
    setIsCheckingScope(true);
    try {
      const result = await checkInstallationScope(
        projectId!,
        planDetails.sectorCodes,
        planDetails.geographyDetails,
        accessToken ?? undefined,
        authUser,
      );
      if (result.addableSiteCount > 0) return true;
      setScopeWarning(
        result.reason ??
          translateOr(
            t,
            "ES_PM_NO_END_USER_SITES_AVAILABLE",
            "There are no end user sites available in the selected geography and sectors.",
          ),
      );
      return false;
    } catch (error) {
      setSaveError(
        extractApiErrorMessage(error) ??
          (error instanceof Error ? error.message : undefined) ??
          translateOr(
            t,
            "ES_PM_SCOPE_CHECK_FAILED",
            "Could not check the end user sites for this selection. Please try again.",
          ),
      );
      return false;
    } finally {
      setIsCheckingScope(false);
    }
  }

  useStepGuard(isPlanHydrated, currentStep, maxAccessibleStep, navigate);

  function goToStep(step: number) {
    if (isNavigationBusy || step < 1 || step > maxAccessibleStep) return;
    void navigate({ search: (prev) => ({ ...prev, step }), replace: true });
  }

  async function handleNext() {
    if (!canGoNext || isNavigationBusy) return;
    setSaveError(undefined);
    // Only a plan about to be created is checked. An existing plan already has its scope; a
    // geography it gains just offers more sites on the next scope download.
    if (!planId) {
      setScopeWarning(undefined);
      if (!(await hasAddableSites())) return;
    }
    // Moving forward is the only point at which a step is persisted. This
    // leaves Back and stepper navigation safely read-only.
    let saved: SavedInstallationPlan;
    try {
      saved = await persistPlan();
    } catch (error) {
      // Without this the rejection is unhandled and the PM sees nothing at all: Next simply
      // re-enables and the step never advances.
      reportSaveError(error);
      return;
    }

    // The plan itself saved; only the reviewer assignment failed. Stay on this step and say so,
    // rather than advancing past a plan that has no reviewer. The id is already in the URL below,
    // so pressing Next again updates and retries the assignment instead of creating a second plan.
    if (saved.reviewerError) {
      void navigate({ search: (prev) => ({ ...prev, planId: saved.plan.id }), replace: true });
      setSaveError(
        extractApiErrorMessage(saved.reviewerError) ??
          translateOr(
            t,
            "ES_PM_REVIEWER_ASSIGNMENT_FAILED",
            "The installation plan was saved, but the Installation Reviewer could not be assigned. Please try again.",
          ),
      );
      return;
    }

    void navigate({ search: () => ({ projectId, planId: saved.plan.id, step: currentStep + 1 }), replace: true });
  }

  function reportSaveError(error: unknown) {
    // A removed place or sector may have gained a scoped site since the locks were read.
    void queryClient.invalidateQueries({ queryKey: pmKeys.planGeographyLocks(planId) });
    setSaveError(
      extractApiErrorMessage(error) ??
        translateOr(t, "ES_PM_INSTALLATION_PLAN_SAVE_FAILED", "Failed to save this installation plan. Please try again."),
    );
  }

  /** A published plan's only edit: dropping places and sectors that hold none of its scoped sites. */
  async function handleSavePublishedPlanDetails() {
    setSaveError(undefined);
    try {
      await persistPlan();
      toast.success(translateOr(t, "ES_PM_PLAN_DETAILS_SAVED", "Installation plan updated"));
    } catch (error) {
      reportSaveError(error);
    }
  }

  async function handleConfirmSubmit() {
    setConfirmError(undefined);
    setIsPublishing(true);
    try {
      // No persistPlan() here, deliberately: by step 4 there's a plan id already, the dates are
      // locked from the moment the plan is first created, and geography/sector/reviewer edits are
      // saved by step 1's Next (step 1 warns that leaving any other way drops them). That made the
      // update a same-data round trip on the one action that's irreversible
      // — pure risk, no effect, since assignments themselves aren't even part of that payload
      // (they're written below, via vendor-assignment/_create).
      const validation = await validateVendorAssignment(planId!, assignments, accessToken ?? undefined, authUser);
      if (!validation.valid) {
        setConfirmError(
          validation.errors
            .map((issue) => issue.message)
            .filter(Boolean)
            .join("\n") ||
            translateOr(
              t,
              "ES_PM_INSTALLATION_PLAN_VALIDATION_FAILED",
              "This installation plan can't be submitted yet. Please review the technician assignments.",
            ),
        );
        return;
      }

      await publishInstallationPlan(planId!, assignments, accessToken ?? undefined, authUser);
      // Publishing flips the plan's status to PUBLISHED, which drives the read-only
      // locking on every step. Without this the cache keeps serving the DRAFT plan.
      await queryClient.invalidateQueries({ queryKey: pmKeys.plans() });
      setConfirmOpen(false);
      setJustPublished(true);
    } catch (error) {
      setConfirmError(
        extractApiErrorMessage(error) ??
          translateOr(
            t,
            "ES_PM_INSTALLATION_PLAN_SUBMIT_FAILED",
            "Failed to submit the installation plan. Please try again.",
          ),
      );
    } finally {
      setIsPublishing(false);
    }
  }

  function renderStepContent() {
    if (currentStep === 1) {
      return (
        <PlanDetailsStep
          value={planDetails}
          onChange={setPlanDetails}
          allowedGeography={allowedPlanGeography}
          projectStartDate={project?.startDate}
          projectEndDate={project?.endDate}
          locked={Boolean(planId)}
          geographyEditable
          lockTarget={planId ? { fieldPlanId: planId } : undefined}
          // A published plan's sectors can only shrink, like its geography.
          allowedSectorCodes={isPublished ? existingPlan?.additionalDetails?.sectorCodes ?? [] : undefined}
          // Locked after publish (the backend refuses it too). On a draft it's kept locked only while
          // the lookup is in flight, so the field doesn't flicker editable before it's filled.
          reviewerLocked={isPublished || (Boolean(planId) && isReviewerLoading)}
          showUnsavedWarning={!isPublished && isPlanDetailsDirty}
        />
      );
    }
    if (currentStep === 2) {
      return (
        <InstallationScopeStep
          planId={planId}
          planCode={existingPlan?.name}
          projectId={projectId}
          projectGeography={projectGeography}
          sectorCodes={planDetails.sectorCodes}
          onChange={setScope}
          onBusyChange={setScopeBusy}
          onScopeApplied={(entries) => {
            setScope(entries);
            setScopeApplied(true);
          }}
        />
      );
    }
    if (currentStep === 3) {
      return (
        <TemplateStep
          planId={planId}
          planCode={existingPlan?.name}
          scope={scope}
          projectGeography={projectGeography}
          value={templates}
          onChange={setTemplates}
          onBusyChange={setTemplateBusy}
          locked={isPublished}
        />
      );
    }
    if (currentStep === 4) {
      return (
        <TechnicianAssignmentStep
          planId={planId}
          planCode={existingPlan?.name}
          value={assignments}
          onChange={setAssignments}
          locked={isPublished}
        />
      );
    }
    return null;
  }

  function renderFooterButtons() {
    if (currentStep < 4) {
      return (
        <Button type="button" size="sm" onClick={handleNext} disabled={!canGoNext || isNavigationBusy}>
          {translateOr(t, "CORE_COMMON_NEXT", "Next")}
        </Button>
      );
    }
    return (
      <Button
        type="button"
        size="sm"
        className="px-5"
        onClick={() => setConfirmOpen(true)}
        disabled={!isAssignmentValid(assignments, assignmentRows) || isNavigationBusy}
      >
        {translateOr(t, "CORE_COMMON_SUBMIT", "Submit")}
      </Button>
    );
  }

  if (justPublished) {
    return (
      <WizardSuccessCard
        title={translateOr(t, "ES_PM_INSTALLATION_PLAN_CREATED", "Installation Plan Created!")}
        itemLabel={translateOr(t, "ES_PM_INSTALLATION_PLAN_NAME", "Installation Plan Name")}
        itemName={existingPlan?.name}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void rawNavigate({ to: employeeHomePath() })}>
              {translateOr(t, "CORE_COMMON_OVERVIEW", "Overview")}
            </Button>
            <Button size="sm" onClick={() => void rawNavigate({ to: pmProjectDetailsPath(), search: { projectId } })}>
              {translateOr(t, "ES_PM_GO_TO_PROJECT", "Go To Project")}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <div className="w-full space-y-6 pb-20">
      <TopBar
        title={translateOr(t, "ES_PM_CREATE_INSTALLATION_PLAN", "Create Installation Plan")}
        breadcrumbs={[
          { label: translateOr(t, "CORE_COMMON_OVERVIEW", "Overview"), to: employeeHomePath() },
          { label: translateOr(t, "ES_PM_MY_PROJECTS", "My Projects"), to: pmMyProjectsPath() },
          {
            label: project?.name ?? translateOr(t, "ES_PM_PROJECT", "Project"),
            to: pmProjectDetailsPath(),
            search: { projectId },
          },
          { label: translateOr(t, "ES_PM_INSTALLATION_PLAN", "Installation Plan") },
        ]}
      />

      {isPublished ? (
        <div className="livelihood-card border border-primary/30 bg-primary/5 p-4 text-sm text-foreground">
          {translateOr(
            t,
            "ES_PM_PLAN_PUBLISHED_GEOGRAPHY_ONLY",
            "This installation plan has been published. Only places and sectors with no end-user site in its scope can still be removed.",
          )}
        </div>
      ) : null}

      <WizardStepper
        steps={STEP_DEFINITIONS}
        currentStep={currentStep}
        onStepChange={goToStep}
        isStepClickable={(step) => step <= maxAccessibleStep}
        allCompleted={isPublished}
      />

      {renderStepContent()}

      {currentStep === 1 && scopeWarning ? (
        <IngestionStatusBlocks status="error" isGuidance errorMessage={scopeWarning} />
      ) : null}

      {saveError ? (
        <p className="text-sm whitespace-pre-line text-destructive wrap-anywhere" role="alert">
          {saveError}
        </p>
      ) : null}

      {isPublished && currentStep === 1 ? (
        <WizardActionFooter>
          <Button
            type="button"
            size="sm"
            className="px-5"
            onClick={() => void handleSavePublishedPlanDetails()}
            disabled={!(isPlanGeographyDirty || isPlanSectorsDirty) || !isPlanDetailsComplete || savePlan.isPending}
          >
            {translateOr(t, "CORE_COMMON_SAVE", "Save")}
          </Button>
        </WizardActionFooter>
      ) : null}

      {!isPublished ? (
        <WizardActionFooter>
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => goToStep(currentStep - 1)}
              disabled={isNavigationBusy}
            >
              {translateOr(t, "CORE_COMMON_BACK", "Back")}
            </Button>
          ) : null}
          {renderFooterButtons()}
        </WizardActionFooter>
      ) : null}

      <ConfirmSubmitDialog
        open={confirmOpen}
        descriptionKey="ES_PM_CONFIRM_SUBMIT_PLAN_DESCRIPTION"
        descriptionFallback="Once this Installation Plan is submitted, you won't be able to add any new end-user sites. You can still remove an existing site, but only if no Installation Report has been submitted for it."
        isSubmitting={savePlan.isPending || isPublishing}
        errorMessage={confirmError}
        onCancel={() => {
          setConfirmError(undefined);
          setConfirmOpen(false);
        }}
        onConfirm={() => void handleConfirmSubmit()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The wizard's data-hydration effects, extracted to their own top-level functions (rather than
// left inline in the component) so each keeps its own, independently readable control flow --
// they are unchanged from what used to run directly inside CreateInstallationPlanPage, moved
// verbatim including every guard and comment below.
// ---------------------------------------------------------------------------------------------

/** Loads an existing plan's own fields into local state once the plan itself -- and the two
 *  sibling queries whose absence would otherwise make the plan look unfinished -- have all
 *  resolved. */
function usePlanDetailsHydration(
  planId: string | undefined,
  existingPlan: InstallationPlan | null | undefined,
  isScopeLoading: boolean,
  isTemplatesLoading: boolean,
  setPlanDetails: Dispatch<SetStateAction<PlanDetailsValue>>,
  setIsPlanHydrated: Dispatch<SetStateAction<boolean>>,
) {
  useEffect(() => {
    if (!planId) {
      setIsPlanHydrated(true);
      return;
    }
    if (!existingPlan) return;
    // The scope and template queries have to settle too, not just the plan. They are separate
    // requests, and until they land `scope`/`templates` are empty, which makes maxAccessibleStep
    // 2 -- so marking the plan hydrated on `existingPlan` alone lets the guard below rewrite a
    // reload of step 3 or 4 back to step 2, with `replace`, losing the history entry.
    if (isScopeLoading || isTemplatesLoading) return;
    // reviewerCode is intentionally left out here and preserved from prior state: the assigned
    // reviewer is never part of this response (see useReviewerHydration below), so resetting it
    // to "" on every hydration would clobber that effect's result if existingPlan ever refetches.
    setPlanDetails((prev) => ({
      geographyDetails: existingPlan.geographyDetails ?? {},
      sectorCodes: existingPlan.additionalDetails?.sectorCodes ?? [],
      reviewerCode: prev.reviewerCode,
      startDate: existingPlan.startDate,
      endDate: existingPlan.endDate,
    }));
    // scope, templates and assignments are intentionally left out here too, for the same reason
    // as reviewerCode: none of them is part of this response (see the hydration hooks below),
    // so resetting them on every hydration would wipe out those effects' results if existingPlan
    // ever refetches.
    setIsPlanHydrated(true);
  }, [existingPlan, planId, isScopeLoading, isTemplatesLoading]);
}

/** The assigned reviewer lives in field-planner-activity's activity_assignments, not on
 *  field-planner's own FieldPlan response, so it can't come from `usePlanDetailsHydration` above
 *  -- it resolves separately and is patched in once available. Guarded on reviewerCode still
 *  being empty so it can't clobber a value the PM has since changed. */
function useReviewerHydration(
  planId: string | undefined,
  assignedReviewer: string | undefined,
  setPlanDetails: Dispatch<SetStateAction<PlanDetailsValue>>,
) {
  useEffect(() => {
    if (!planId || !assignedReviewer) return;
    setPlanDetails((prev) => (prev.reviewerCode ? prev : { ...prev, reviewerCode: assignedReviewer }));
  }, [assignedReviewer, planId]);
}

/** The plan's real Installation Scope lives in field_plan_facilities, not on field-planner's own
 *  FieldPlan response either -- same reason, own effect. Guarded on scope still being empty so a
 *  scope just uploaded this session (or a re-upload with different entries) isn't clobbered by a
 *  stale fetch resolving late. */
function useScopeHydration(
  planId: string | undefined,
  savedScope: ScopeValue | undefined,
  setScope: Dispatch<SetStateAction<ScopeValue>>,
) {
  useEffect(() => {
    if (!planId || !savedScope || savedScope.length === 0) return;
    setScope((prev) => (prev.length > 0 ? prev : savedScope));
  }, [savedScope, planId]);
}

/** Same reasoning again for the plan's already-uploaded IC report templates --
 *  field_plan_template rows live outside field-planner's FieldPlan object too. */
function useTemplatesHydration(
  planId: string | undefined,
  savedTemplates: TemplateValue | undefined,
  setTemplates: Dispatch<SetStateAction<TemplateValue>>,
) {
  useEffect(() => {
    if (!planId || !savedTemplates || savedTemplates.length === 0) return;
    setTemplates((prev) => (prev.length > 0 ? prev : savedTemplates));
  }, [savedTemplates, planId]);
}

/** ...and once more for the saved technician assignments. `additionalDetails.assignments` is
 *  never echoed back by field-planner either, so reopening a draft used to land on step 4 with
 *  every dropdown blank even though the vendors had been saved. They do come back on the
 *  vendor-assignment search already loaded by the page -- each asset carries its own vendor
 *  fields -- so they're flattened out of that rather than fetched again. Same empty-guard as the
 *  others. */
function useAssignmentsHydration(
  planId: string | undefined,
  savedAssignments: AssignmentValue,
  setAssignments: Dispatch<SetStateAction<AssignmentValue>>,
) {
  useEffect(() => {
    if (!planId || !savedAssignments || savedAssignments.length === 0) return;
    setAssignments((prev) => (prev.length > 0 ? prev : savedAssignments));
  }, [savedAssignments, planId]);
}

/** A new plan's dates default to the project's own start date and one month past it, capped at
 *  the project's own end date if that's sooner -- never left blank for the PM to fill in from
 *  scratch. Only for a brand-new plan (an existing plan's own dates already win via
 *  `usePlanDetailsHydration` above), and only once: guarded on startDate still being unset so it
 *  doesn't clobber a date the PM has since edited. */
function useDefaultPlanDates(
  planId: string | undefined,
  project: Project | null | undefined,
  planDetailsStartDate: number | undefined,
  setPlanDetails: Dispatch<SetStateAction<PlanDetailsValue>>,
) {
  useEffect(() => {
    if (planId || !project?.startDate || planDetailsStartDate !== undefined) return;
    const oneMonthOut = new Date(project.startDate);
    oneMonthOut.setMonth(oneMonthOut.getMonth() + 1);
    const defaultEndDate = project.endDate ? Math.min(oneMonthOut.getTime(), project.endDate) : oneMonthOut.getTime();
    setPlanDetails((prev) => ({ ...prev, startDate: project.startDate, endDate: defaultEndDate }));
  }, [project, planId, planDetailsStartDate]);
}

/** Redirects back to the furthest step the plan has actually earned whenever the URL points past
 *  it -- a reload or a hand-edited link to a later step, most often. */
function useStepGuard(
  isPlanHydrated: boolean,
  currentStep: number,
  maxAccessibleStep: number,
  navigate: PlanNavigate,
) {
  useEffect(() => {
    if (!isPlanHydrated || currentStep <= maxAccessibleStep) return;
    void navigate({ search: (prev) => ({ ...prev, step: maxAccessibleStep }), replace: true });
  }, [currentStep, isPlanHydrated, maxAccessibleStep, navigate]);
}

/** Sectors compared as a set, ignoring order. */
function sectorCodesKey(sectorCodes: string[] | undefined): string {
  return [...(sectorCodes ?? [])].sort((a, b) => a.localeCompare(b)).join(",");
}
